import { combineReducers, Reducer } from "redux";

import { SharedActions } from "../flux/actions/sharedActions";
import * as LocalStorageReducer from "./localStorageReducers";
import * as R from "./reducers";

type MessagesState = IKeyedState<IMessageThread>;

const SCOPED_MESSAGING_ACTIONS = [
  SharedActions.Messaging.GetForEnquiry,
  // SharedActions.Messaging.MarkAllRead, // No client side state changes in response to this
  SharedActions.Messaging.SaveDraft,
  SharedActions.Messaging.SaveSnippet,
  SharedActions.Messaging.Send,
];

/**
 * Generate a reducer to handle messaging actions relating to a single enquiry
 */
const getMessageThreadReducer = (userId: number, enqId: number) => {
  // Must be unique for user-enquiry pairing - in future multiple users will be able to access the same enquiry
  const cacheKey = `user-${userId}-enq-${enqId}-draft`;
  const cacheKeySnippet = `user-${userId}-enq-${enqId}-snippet`;

  return combineReducers<IMessageThread>({
    draft: R.chain(
      LocalStorageReducer.initialiseState(cacheKey, (data) => data),
      R.fromPayload<string, string>(SharedActions.Messaging.SaveDraft, x => x),
      R.when(SharedActions.Messaging.Send, R.clear()),
      R.when(SharedActions.Messaging.SaveDraft, LocalStorageReducer.serialiseState(cacheKey, (draft) => draft)),
    ),
    snippets: R.chain(
      LocalStorageReducer.initialiseState(cacheKeySnippet, (data: string): number[] => {
        if (data == null) {
          return null;
        }
        const newData: number[] = data.split(",").map(Number); // convert comma-separated string into an array
        return newData;
      }),
      R.appendPayload<number, number>(SharedActions.Messaging.SaveSnippet, (x: number) => x), // uses concat to append id into existing list of ids
      R.when(SharedActions.Messaging.Send, R.clear(() => R.SnippetsState.clearState(cacheKeySnippet))),
      R.when(
        SharedActions.Messaging.SaveSnippet,
        LocalStorageReducer.serialiseState(cacheKeySnippet, (draft) => (draft.join()[0] === ",") ? draft.join().substr(1) : draft.join()),
      ),
    ),
    thread: R.fromApi<IMessagesOutput, IMessage[]>(SharedActions.Messaging.GetForEnquiry, x => x.messages),
  });
};

/**
 * Reducer to handle message threads keyed by enquiryId.
 */
const messagingReducer: Reducer<MessagesState> = (state: MessagesState, action: IStoreAction<any>): MessagesState => {
  if (R.isMatch(action.type, SCOPED_MESSAGING_ACTIONS)) {
    const enquiryId: number = action.meta ? action.meta.enquiryId : null;
    if (enquiryId == null) {
      // TODO enforce that these props exist through typed actions
      throw new Error(`Messaging actions like '${action.type}' must be dispatched with a context shaped { enquiryId: number }`);
    }

    const currentUserState = R.Auth.getUserState();
    if (currentUserState == null || currentUserState.data == null || !currentUserState.data.id) {
      throw new Error("User state is not in LocalStorage - cannot dispatch message actions while not logged in.");
    }

    const messageThreadReducer = getMessageThreadReducer(currentUserState.data.id, enquiryId);

    // Apply the messageThreadReducer to only this enquiry id's message thread
    const scopedReducer = R.scope<MessagesState, IMessageThread>(x => x.data[enquiryId], messageThreadReducer);

    state.data[enquiryId] = scopedReducer(state, action);

    return {
      status: "OK",
      data: state.data,
      context: action.meta,
    };
  }

  return state || { data: {}, status: "OK", context: null };
};

export default messagingReducer;
